import { useLang } from "../../context/LanguageContext";
import { nature } from "../../lib/api";
import { Mountain, TreePine, Waves, Sparkles } from "lucide-react";

/**
 * Nature · Pusterwald — dedicated section celebrating the alpine surroundings.
 * Sits between Location and Reviews on the homepage.
 */
export default function Nature() {
  const { lang } = useLang();
  const t = lang === "de" ? DE : EN;

  return (
    <section
      id="nature"
      data-testid="nature-section"
      className="section-pad relative bg-[hsl(var(--sage))] overflow-hidden"
    >
      <div className="container-x relative" style={{ zIndex: 5 }}>
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-5">{t.eyebrow}</div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-[hsl(var(--foreground))] whitespace-pre-line">
              {t.title}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-[hsl(var(--foreground))]/88 leading-relaxed">
              {t.body}
            </p>
          </div>
        </div>

        {/* Feature grid — big landscape shot + 4 stat pills */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-14">
          <div className="lg:col-span-8 aspect-[16/10] lg:aspect-auto overflow-hidden border border-[hsl(var(--foreground))]/10 group">
            <img
              src={nature.mountainLake}
              alt={lang === "de" ? "Bergsee in den steirischen Alpen" : "Mountain lake in the Styrian Alps"}
              loading="lazy"
              className="w-full h-full object-cover gallery-img"
            />
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-6">
            {t.stats.map((s, i) => (
              <div
                key={i}
                data-testid={`nature-stat-${i}`}
                className="bg-[hsl(var(--card))] backdrop-blur-sm border border-[hsl(var(--border))] p-6 flex flex-col justify-between min-h-[130px] shadow-sm"
              >
                <div className="text-[hsl(var(--accent))]">
                  {[Mountain, TreePine, Waves, Sparkles][i] && (() => {
                    const Icon = [Mountain, TreePine, Waves, Sparkles][i];
                    return <Icon className="w-6 h-6" strokeWidth={1.5} />;
                  })()}
                </div>
                <div>
                  <div className="font-serif text-3xl md:text-4xl text-[hsl(var(--foreground))] leading-none">{s.value}</div>
                  <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--foreground))]/78">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Nature strip — 4 curated moments */}
        <div>
          <div className="overline mb-6">{t.moments}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {t.tiles.map((tile, i) => (
              <figure
                key={i}
                data-testid={`nature-tile-${i}`}
                className="group relative overflow-hidden aspect-[4/5] bg-[hsl(var(--muted))]"
              >
                <img
                  src={[nature.alpineMeadow, nature.forestPath, nature.waterfall, nature.wildlifeDeer][i]}
                  alt={tile.label}
                  loading="lazy"
                  className="w-full h-full object-cover gallery-img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(178_60%_6%)]/90 via-transparent to-transparent" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))]">{tile.overline}</div>
                  <div className="mt-1.5 font-serif text-xl md:text-2xl text-white leading-tight">{tile.label}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const DE = {
  eyebrow: "Natur um Pusterwald",
  title: "Steirische Alpen.\nDirekt vor der Tür.",
  body:
    "Pusterwald liegt eingebettet in die Wölzer Tauern auf 1.070 m Seehöhe — umgeben von unberührter Bergwelt, glasklaren Bächen und weitem Almland. Rehe, Gämsen und Adler sind hier keine Ausnahme, sondern täglicher Begleiter.",
  stats: [
    { value: "2 356 m", label: "Höchster Gipfel · Kleiner Rosennock" },
    { value: "80 km",   label: "Wanderwege in der Region" },
    { value: "17",      label: "Almen & Berghütten" },
    { value: "1 070 m", label: "Chalet-Seehöhe" },
  ],
  moments: "Momente in der Natur",
  tiles: [
    { overline: "Sommer",  label: "Blühende Almwiesen" },
    { overline: "Herbst",  label: "Nadelwald-Wanderungen" },
    { overline: "Frühling", label: "Wasserfälle & Gumpen" },
    { overline: "Wildtiere", label: "Rehe, Gämse, Adler" },
  ],
};

const EN = {
  eyebrow: "Nature around Pusterwald",
  title: "Styrian Alps.\nRight outside the door.",
  body:
    "Pusterwald sits at 1,070 m in the Wölzer Tauern range — surrounded by untouched mountains, crystal-clear streams and vast alpine pastures. Deer, chamois and eagles aren't the exception here, they're daily companions.",
  stats: [
    { value: "2,356 m", label: "Highest peak · Kleiner Rosennock" },
    { value: "80 km",   label: "Hiking trails in the region" },
    { value: "17",      label: "Alpine huts & pastures" },
    { value: "1,070 m", label: "Chalet elevation" },
  ],
  moments: "Moments in nature",
  tiles: [
    { overline: "Summer", label: "Blooming alpine meadows" },
    { overline: "Autumn", label: "Pine forest walks" },
    { overline: "Spring", label: "Waterfalls & pools" },
    { overline: "Wildlife", label: "Deer, chamois, eagles" },
  ],
};
